import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import {
  ProcessFlow,
  ProcessFlowCreate,
  ProcessFlowUpdate,
  ProcessFlowStep,
  ProcessFlowStepCreate,
  ProcessFlowStepUpdate,
  ProcessFlowExecution,
  ProcessFlowExecutionCreate,
  ProcessFlowExecutionComplete,
  ProcessFlowAnalytics,
  ProcessFlowPerformance,
} from '../../types/processFlow';

/**
 * Service for process flow management operations
 */
export class ProcessFlowService extends BaseService {
  // ==================== Flow Management ====================

  /**
   * Get all process flows
   */
  static async getFlows(params?: {
    company_id?: string;
    flow_type?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ProcessFlow[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.company_id) queryParams.append('company_id', params.company_id);
      if (params?.flow_type) queryParams.append('flow_type', params.flow_type);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());

      const endpoint = queryParams.toString()
        ? `${API_ENDPOINTS.PROCESS_FLOWS.BASE}?${queryParams.toString()}`
        : API_ENDPOINTS.PROCESS_FLOWS.BASE;

      const response = await this.get<ProcessFlow[] | { flows: ProcessFlow[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'flows' in response) {
        return (response as { flows: ProcessFlow[] }).flows;
      }
      return [];
    } catch (error) {
      console.error('[ProcessFlowService] Error fetching flows:', error);
      return [];
    }
  }

  /**
   * Get flow by ID
   */
  static async getFlowById(flowId: string): Promise<ProcessFlow | null> {
    try {
      return await this.get<ProcessFlow>(API_ENDPOINTS.PROCESS_FLOWS.BY_ID(flowId));
    } catch (error) {
      console.error('[ProcessFlowService] Error fetching flow:', error);
      return null;
    }
  }

  /**
   * Create a new process flow
   */
  static async createFlow(data: ProcessFlowCreate): Promise<ProcessFlow> {
    return this.post<ProcessFlow>(
      API_ENDPOINTS.PROCESS_FLOWS.BASE,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Update a process flow
   */
  static async updateFlow(flowId: string, data: ProcessFlowUpdate): Promise<ProcessFlow> {
    return this.put<ProcessFlow>(
      API_ENDPOINTS.PROCESS_FLOWS.BY_ID(flowId),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Delete a process flow
   */
  static async deleteFlow(flowId: string): Promise<void> {
    return this.delete(API_ENDPOINTS.PROCESS_FLOWS.BY_ID(flowId));
  }

  // ==================== Step Management ====================

  /**
   * Get all steps for a flow
   */
  static async getFlowSteps(flowId: string): Promise<ProcessFlowStep[]> {
    try {
      const response = await this.get<ProcessFlowStep[] | { steps: ProcessFlowStep[] }>(
        API_ENDPOINTS.PROCESS_FLOWS.STEPS(flowId)
      );
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'steps' in response) {
        return (response as { steps: ProcessFlowStep[] }).steps;
      }
      return [];
    } catch (error) {
      console.error('[ProcessFlowService] Error fetching flow steps:', error);
      return [];
    }
  }

  /**
   * Add a step to a flow
   */
  static async addFlowStep(flowId: string, data: ProcessFlowStepCreate): Promise<ProcessFlowStep> {
    return this.post<ProcessFlowStep>(
      API_ENDPOINTS.PROCESS_FLOWS.STEPS(flowId),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Update a flow step
   */
  static async updateFlowStep(
    flowId: string,
    stepId: string,
    data: ProcessFlowStepUpdate
  ): Promise<ProcessFlowStep> {
    return this.put<ProcessFlowStep>(
      API_ENDPOINTS.PROCESS_FLOWS.STEP_BY_ID(flowId, stepId),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Delete a flow step
   */
  static async deleteFlowStep(flowId: string, stepId: string): Promise<void> {
    return this.delete(API_ENDPOINTS.PROCESS_FLOWS.STEP_BY_ID(flowId, stepId));
  }

  // ==================== Execution Management ====================

  /**
   * Execute a process flow
   */
  static async executeFlow(flowId: string, data?: ProcessFlowExecutionCreate): Promise<ProcessFlowExecution> {
    return this.post<ProcessFlowExecution>(
      API_ENDPOINTS.PROCESS_FLOWS.EXECUTE(flowId),
      (data || {}) as unknown as Record<string, unknown>
    );
  }

  /**
   * Get executions for a flow
   */
  static async getFlowExecutions(
    flowId: string,
    params?: {
      status?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<ProcessFlowExecution[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());

      const endpoint = queryParams.toString()
        ? `${API_ENDPOINTS.PROCESS_FLOWS.EXECUTIONS(flowId)}?${queryParams.toString()}`
        : API_ENDPOINTS.PROCESS_FLOWS.EXECUTIONS(flowId);

      const response = await this.get<ProcessFlowExecution[] | { executions: ProcessFlowExecution[] }>(endpoint);
      
      if (Array.isArray(response)) {
        return response;
      }
      if (response && typeof response === 'object' && 'executions' in response) {
        return (response as { executions: ProcessFlowExecution[] }).executions;
      }
      return [];
    } catch (error) {
      console.error('[ProcessFlowService] Error fetching flow executions:', error);
      return [];
    }
  }

  /**
   * Complete a flow execution
   */
  static async completeExecution(
    flowId: string,
    executionId: string,
    data: ProcessFlowExecutionComplete
  ): Promise<ProcessFlowExecution> {
    return this.post<ProcessFlowExecution>(
      API_ENDPOINTS.PROCESS_FLOWS.COMPLETE_EXECUTION(flowId, executionId),
      data as unknown as Record<string, unknown>
    );
  }

  // ==================== Analytics ====================

  /**
   * Get process flow analytics
   */
  static async getAnalytics(params?: {
    company_id?: string;
    days?: number;
  }): Promise<ProcessFlowAnalytics | null> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.company_id) queryParams.append('company_id', params.company_id);
      if (params?.days) queryParams.append('days', params.days.toString());

      const endpoint = queryParams.toString()
        ? `${API_ENDPOINTS.PROCESS_FLOWS.ANALYTICS}?${queryParams.toString()}`
        : API_ENDPOINTS.PROCESS_FLOWS.ANALYTICS;

      return await this.get<ProcessFlowAnalytics>(endpoint);
    } catch (error) {
      console.error('[ProcessFlowService] Error fetching analytics:', error);
      return null;
    }
  }

  /**
   * Get flow performance metrics
   */
  static async getFlowPerformance(
    flowId: string,
    days?: number
  ): Promise<ProcessFlowPerformance | null> {
    try {
      const endpoint = days
        ? `${API_ENDPOINTS.PROCESS_FLOWS.PERFORMANCE(flowId)}?days=${days}`
        : API_ENDPOINTS.PROCESS_FLOWS.PERFORMANCE(flowId);

      return await this.get<ProcessFlowPerformance>(endpoint);
    } catch (error) {
      console.error('[ProcessFlowService] Error fetching flow performance:', error);
      return null;
    }
  }
}

